import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

type Category = {
  id: string;
  slug: string;
  title: {
    el: string;
    en: string;
  };
  image: string;
  industrySlug: string;
};

type HomepageCarouselProps = {
  categories: Category[];
  language: 'el' | 'en';
  t: (el: string, en: string) => string;
};

export const HomepageCarousel = ({ categories, language, t }: HomepageCarouselProps) => {
  const autoplayRef = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true })
  );
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      slidesToScroll: 1,
      skipSnaps: false,
      duration: 25,
      containScroll: 'trimSnaps'
    },
    [autoplayRef.current]
  );
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') scrollPrev();
      if (e.key === 'ArrowRight') scrollNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect, scrollPrev, scrollNext]);

  const toggleAutoplay = useCallback(() => {
    const autoplay = autoplayRef.current;
    if (!autoplay) return;

    if (autoplay.isPlaying()) {
      autoplay.stop();
      setIsPlaying(false);
    } else {
      autoplay.play();
      setIsPlaying(true);
    }
  }, []);

  return (
    <div className="relative max-w-6xl mx-auto">
      {/* Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3 md:gap-4">
          {categories.map((category, index) => {
            const isActive = index === selectedIndex;
            
            return (
              <div 
                key={category.id} 
                className={`flex-[0_0_100%] md:flex-[0_0_45%] lg:flex-[0_0_35%] min-w-0 transition-all duration-500 ease-in-out ${
                  isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                }`}
              >
                <Card className="hover:shadow-xl transition-all duration-300 group overflow-hidden h-full">
                  <CardContent className="p-0 h-full">
                    <Link 
                      to={`/industries/${category.industrySlug}/${category.slug}`} 
                      className="block h-full"
                      onClick={() => autoplayRef.current?.stop()}
                    >
                      <div className="flex flex-col h-full">
                        <div className="w-full aspect-[4/3] relative overflow-hidden">
                          <img
                            src={category.image}
                            alt={category.title[language]}
                            className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-500 ${
                              isActive ? 'grayscale-0' : 'grayscale'
                            }`}
                            loading="lazy"
                            width="800"
                            height="600"
                          />
                        </div>
                        <div className="p-4 flex flex-col bg-card">
                          <h3 className="text-lg md:text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                            {category.title[language]}
                          </h3>
                          <Button variant="ghost" className="group/btn p-0 h-auto hover:bg-transparent justify-start">
                            <span className="mr-2 text-sm">{t('Περισσότερα', 'Learn More')}</span>
                            <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </div>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile */}
      <Button
        variant="outline"
        size="icon"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background backdrop-blur-sm shadow-lg"
        onClick={scrollPrev}
        aria-label={t('Προηγούμενη κατηγορία', 'Previous category')}
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>
      
      <Button
        variant="outline"
        size="icon"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background backdrop-blur-sm shadow-lg"
        onClick={scrollNext}
        aria-label={t('Επόμενη κατηγορία', 'Next category')}
      >
        <ChevronRight className="h-6 w-6" />
      </Button>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-1 mt-6">
        {categories.map((_, index) => (
          <button
            key={index}
            className="p-2 group"
            onClick={() => scrollTo(index)}
            aria-label={`${t('Μετάβαση στην κατηγορία', 'Go to category')} ${index + 1}`}
          >
            <span className={`block h-3 rounded-full transition-all duration-300 ${
              index === selectedIndex 
                ? 'bg-primary w-8' 
                : 'bg-muted-foreground/30 group-hover:bg-muted-foreground/50 w-3'
            }`} />
          </button>
        ))}
      </div>
    </div>
  );
};